import { fromFetch } from "rxjs/fetch";
import * as RX from "rxjs";
import { Map } from "../models/map";
import { Failure } from "../helpers/failure";
import { memoize } from "lodash-es";
import * as Log from "../helpers/browserConsole";
import localforage from "localforage";
import { collapse } from "../helpers/rxjs-helpers";
import { option } from "fp-ts";
import { Option } from "fp-ts/lib/Option";
import { Language } from "../models/language";
import { pipe } from "fp-ts/lib/function";
import { Exhibitor, ExhibitorMeetingSettings } from "../models/old/exhibitor";
import { ApiResult } from "../models/api/apiResult";
import { ApiTemplate } from "../models/api/template";
import { ApiFilterPanel, ApiSearchView } from "../models/searchView";
import { ApiExhibition } from "../models/api/exhibition";
import { ApiElement, ApiModule } from "../models/api/module";
import { safeUdstillerguideAccess } from "../app/getSiteConfig";

const APIPrefix: string = "ug/v1";

export type UserFavorites = number[];

export class ApiService {
  baseURL: string;
  language: Language;
  pageId: Option<number>;

  constructor(baseURL: string, language: Language) {
    this.baseURL = baseURL;
    this.language = language;

    this.pageId = option.fromNullable(safeUdstillerguideAccess()?.pageId);
  }

  public getUserId = memoize(() => {
    return this.getAuthed$(
      this.makeRequestURL(`${APIPrefix}/user/userId`)
    ).pipe(
      RX.switchMap((response) => response.text()), // TODO: wrap getAuthed in higher order functions so we can reuse getText and getJson
      RX.map((responseText) => JSON.parse(responseText)),
      RX.map((x: { success: boolean; data: string }) => {
        return x.success == true ? option.some(x.data) : option.none;
      }),
      RX.distinctUntilChanged(),
      RX.shareReplay(1)
    );
  });

  public getUserFavorites(id: string) {
    return this.getJson$<UserFavorites>(
      this.makeRequestURL(`${APIPrefix}/user/${id}/favorites`)
    );
  }

  public getExhibitor(id: string) {
    return this.getJson$<Exhibitor>(
      this.makeRequestURL(`${APIPrefix}/exhibitor/${id}`)
    );
  }
  public getExhibitorMeetingSettings(id: string) {
    return this.getJson$<ExhibitorMeetingSettings>(
      this.makeRequestURL(`${APIPrefix}/exhibitor/${id}/meeting-settings`)
    );
  }
  public getExhibitorAvailableTimeslots(id: string) {
    return this.getJson$(
      this.makeRequestURL(`${APIPrefix}/exhibitor/${id}/timeslots/available`)
    );
  }
  public getExhibitionFromSlug(slug: string) {
    const queryParams = new URLSearchParams({
      lang: this.language,
    });
    return this.getJson$<ApiExhibition>(
      this.makeRequestURL(
        `${APIPrefix}/exhibition-from-slug/${slug}`,
        queryParams
      )
    );
  }
  public getExhibitionFromId(id: number) {
    const queryParams = new URLSearchParams({
      lang: this.language,
    });
    return this.getJson$<ApiExhibition>(
      this.makeRequestURL(`${APIPrefix}/exhibition-from-id/${id}`, queryParams)
    );
  }
  public getExhibitionMaps(id: number) {
    const queryParams = new URLSearchParams({
      lang: this.language,
    });
    return this.getApiDataFromObject<{ maps: Map[] }, "maps">(
      `${APIPrefix}/exhibition/${id}/map`,
      queryParams,
      "maps"
    );
  }

  public getModules(exhibitionId: number) {
    const queryParams = new URLSearchParams({
      lang: this.language,
    });
    return this.getApiDataFromObject<{ postTypes: ApiModule[] }, "postTypes">(
      `${APIPrefix}/exhibition/${exhibitionId}/post-type`,
      queryParams,
      "postTypes"
    );
  }

  public getModuleElements(exhibitionId: number, moduleId: string) {
    const queryParams = new URLSearchParams({
      lang: this.language,
    });

    return this.getApiDataFromObject<{ elements: ApiElement[] }, "elements">(
      `${APIPrefix}/exhibition/${exhibitionId}/post-type/${moduleId}/element`,
      queryParams,
      "elements"
    );
  }

  public getElementThumbnailTemplate(exhibitionId: number, moduleId: string) {
    return this.getTemplate(exhibitionId, moduleId, "thumbnail");
  }
  public getElementSummaryTemplate(exhibitionId: number, moduleId: string) {
    return this.getTemplate(exhibitionId, moduleId, "summary");
  }
  public getElementDetailTemplate(exhibitionId: number, moduleId: string) {
    return this.getTemplate(exhibitionId, moduleId, "detail");
  }

  private getTemplate(
    exhibitionId: number,
    moduleId: string,
    templateName: string
  ) {
    return this.getJson$<ApiTemplate>(
      this.makeRequestURL(
        `${APIPrefix}/exhibition/${exhibitionId}/post-type/${moduleId}/template/${templateName}`,
        new URLSearchParams({
          lang: this.language,
        })
      )
    );
  }

  public getSearchView(searchViewEncodedId: string, exhibitionId?: number) {
    const params = pipe(
      this.pageId,
      option.match(
        () => ({
          lang: this.language,
          ...(exhibitionId !== undefined
            ? { exhibitionId: exhibitionId.toString() }
            : {}),
        }),
        (pageId) => ({
          pageId: pageId.toString(),
          lang: this.language,
          ...(exhibitionId !== undefined
            ? { exhibitionId: exhibitionId.toString() }
            : {}),
        })
      )
    );
    return this.getJson$<ApiSearchView>(
      this.makeRequestURL(
        `${APIPrefix}/app/searchview/${searchViewEncodedId}`,
        new URLSearchParams(params)
      )
    );
  }

  public getFilterPanel(exhibitionId: number, searchViewId: number) {
    return this.getJson$<ApiFilterPanel>(
      this.makeRequestURL(
        `${APIPrefix}/exhibition/${exhibitionId}/searchview/${searchViewId}/filterpanel`,
        new URLSearchParams({
          lang: this.language,
        })
      )
    );
  }

  makeRequestURL = (
    endpoint: string,
    queryParams: URLSearchParams = new URLSearchParams()
  ) => {
    return [this.baseURL, endpoint, "?", queryParams.toString()].join("");
  };

  private getApiDataFromObject<T, K extends keyof T>(
    url: string,
    queryParams: URLSearchParams,
    key: K
  ): RX.Observable<T[K]> {
    const response = this.getJson$<T>(this.makeRequestURL(url, queryParams));
    return response.pipe(RX.map((data: T) => data[key]));
  }

  private getAuthed$ = (url: string) => {
    return fromFetch(url).pipe(
      RX.map((response) => {
        if (!response.ok) {
          throw `There was an error with status ${response.status} on request for ${url}`;
        } else {
          return response;
        }
      })
    );
  };
  private get$ = (url: string) => {
    return fromFetch(url, { credentials: "omit", mode: "cors" }).pipe(
      RX.map((response) => {
        if (!response.ok) {
          throw `There was an error with status ${response.status} on request for ${url}`;
        } else {
          return response;
        }
      })
    );
  };
  private getText$ = memoize((url: string) => {
    const cachedValue$ = RX.from(localforage.getItem<string>(url)).pipe(
      RX.map(option.fromNullable),
      collapse
    );

    const ApiResponse$ = this.get$(url).pipe(
      RX.catchError((_) => {
        return RX.of();
      }),
      RX.switchMap((response) => response.text()),
      RX.tap((responseText) => {
        try {
          localforage.setItem(url, responseText);
        } catch (e) {
          Log.warn("not enough cache space in localstorage");
        }
      })
    );

    return RX.concat(cachedValue$, ApiResponse$).pipe(
      RX.distinctUntilChanged(),
      RX.throwIfEmpty(() => {
        Log.error("Failed to retrieve:", url);
        Failure("Network error: Please reconnect and refresh");
      }),
      RX.shareReplay(1)
    );
  });
  private getJson$: <T>(url: string) => RX.Observable<T> = (url: string) => {
    return this.getText$(url).pipe(
      RX.map((responseText) => JSON.parse(responseText)),
      RX.tap((apiResult: ApiResult<any>) => {
        if (apiResult.success == false) Log.warn(apiResult.data);
      }),
      RX.filter((apiResult: ApiResult<any>) => apiResult.success),
      RX.map((apiResult) => apiResult.data)
    );
  };
}
